import React, { useEffect, useState } from 'react'
import { supabase } from '../../../supabaseClient'

export default function InventorySummary({ branchId, onViewFull }) {
    const [stats, setStats] = useState(null)
    const [flaggedItems, setFlaggedItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [flaggedPage, setFlaggedPage] = useState(1)
    const FLAGGED_PAGE_SIZE = 5

    useEffect(() => {
        if (!branchId) return

        const fetchStats = async () => {
            try {
                const { data: inventoryRows } = await supabase
                    .schema('inventory')
                    .from('branch_inventory')
                    .select('id, current_stock, reorder_level, ingredient_id')
                    .eq('branch_id', branchId)
                    .eq('is_active', true)

                const rows = inventoryRows || []

                const ingredientIdsForReservations = rows.map(r => r.ingredient_id).filter(Boolean)
                let reservedMap = {}
                if (ingredientIdsForReservations.length > 0) {
                    const { data: reservations } = await supabase
                        .schema('inventory')
                        .from('stock_reservations')
                        .select('ingredient_id, reserved_quantity')
                        .eq('branch_id', branchId)
                        .eq('status', 'reserved')
                        .in('ingredient_id', ingredientIdsForReservations)
                    ;(reservations || []).forEach(r => {
                        reservedMap[r.ingredient_id] = (reservedMap[r.ingredient_id] || 0) + Number(r.reserved_quantity || 0)
                    })
                }
                const ingredientIds = [...new Set(rows.map(r => r.ingredient_id).filter(Boolean))]
                let ingredientsMap = {}
                if (ingredientIds.length > 0) {
                    const { data: ingredientsData, error: ingredientsError } = await supabase
                        .schema('inventory')
                        .from('ingredients')
                        .select('ingredient_id, name, unit')
                        .in('ingredient_id', ingredientIds)
                    console.log('[InventorySummary] ingredientIds:', ingredientIds)
                    console.log('[InventorySummary] ingredientsData:', ingredientsData)
                    console.log('[InventorySummary] ingredientsError:', ingredientsError)
                    ingredientsMap = Object.fromEntries(
                        (ingredientsData || []).map(ing => [ing.ingredient_id, ing])
                    )
                }

                const items = rows.map(r => ({
                    ...r,
                    available_stock: Number(r.current_stock) - (reservedMap[r.ingredient_id] || 0),
                    ingredients: ingredientsMap[r.ingredient_id] || null
                }))

                const lowStock = items.filter(i =>
                    i.available_stock > 0 &&
                    i.available_stock <= Number(i.reorder_level)
                )
                const outOfStock = items.filter(i => i.available_stock <= 0)

                setStats({
                    totalItems: items.length,
                    lowStockCount: lowStock.length,
                    outOfStockCount: outOfStock.length,
                })

                const flagged = [
                    ...outOfStock,
                    ...lowStock.filter(i => i.available_stock > 0)
                ]

                setFlaggedItems(flagged)
                setFlaggedPage(1)
            } catch (err) {
                console.error('InventorySummary fetch error:', err)
            } finally {
                setLoading(false)
            }
        }
        fetchStats()
    }, [branchId])

    const flaggedTotalPages = Math.max(1, Math.ceil(flaggedItems.length / FLAGGED_PAGE_SIZE))
    const paginatedFlaggedItems = flaggedItems.slice(
        (flaggedPage - 1) * FLAGGED_PAGE_SIZE,
        flaggedPage * FLAGGED_PAGE_SIZE
    )

    const getStockStyle = (item) => {
        if (item.available_stock <= 0)
            return { bar: 'bg-red-400', text: 'text-red-600', bg: 'bg-red-50', barPct: 0 }

        const pct = Math.min(100,
            (item.available_stock / Math.max(Number(item.reorder_level), 1)) * 100
        )
        return { bar: 'bg-amber-400', text: 'text-amber-600', bg: 'bg-amber-50', barPct: pct }
    }

    if (!branchId && !loading) {
        return null
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 48 48" className="text-orange-600">
                            <path fill="currentColor" d="M26.321 4.832a6.25 6.25 0 0 0-4.642 0L6.672 10.835A4.25 4.25 0 0 0 4 14.78v18.438a4.25 4.25 0 0 0 2.672 3.945l15.007 6.003a6.25 6.25 0 0 0 4.642 0l15.007-6.003A4.25 4.25 0 0 0 44 33.219V14.781a4.25 4.25 0 0 0-2.672-3.946zm-3.714 2.321a3.75 3.75 0 0 1 2.786 0l13.991 5.597l-6.009 2.403L17.991 9zm-7.982 3.193L30.009 16.5L24 18.903L8.616 12.75zM6.509 14.6l16.241 6.496V40.9l-.143-.054L7.6 34.843a1.75 1.75 0 0 1-1.1-1.625V14.781q0-.092.01-.181m18.74 26.3V21.096L41.49 14.6q.01.09.01.18v18.438a1.75 1.75 0 0 1-1.1 1.625l-15.007 6.003z" />
                        </svg>
                    </div>
                    <h3 className="font-semibold text-gray-800 text-sm">Inventory Management</h3>
                </div>
                <button
                    onClick={onViewFull}
                    className="text-xs text-[#A0826D] hover:text-[#8B5A3C] font-medium flex items-center gap-1 transition-colors">
                    View Full
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                </button>
            </div>

            <div className="p-5 space-y-4">
                {loading ? (
                    <div className="space-y-3">
                        <div className="flex gap-3">
                            {[1, 2, 3].map(i => <div key={i} className="flex-1 h-16 bg-gray-100 rounded-lg animate-pulse" />)}
                        </div>
                        <div className="h-24 bg-gray-100 rounded-lg animate-pulse" />
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <div className="bg-orange-50 rounded-lg p-3">
                                <p className="text-xs text-orange-600 font-medium mb-1">Total Items</p>
                                <p className="text-2xl font-bold text-orange-700">{stats?.totalItems ?? 0}</p>
                            </div>
                            <div className="bg-amber-50 rounded-lg p-3">
                                <p className="text-xs text-amber-600 font-medium mb-1">Low Stock</p>
                                <p className="text-2xl font-bold text-amber-700">{stats?.lowStockCount ?? 0}</p>
                            </div>
                            <div className="bg-red-50 rounded-lg p-3">
                                <p className="text-xs text-red-600 font-medium mb-1">Out of Stock</p>
                                <p className="text-2xl font-bold text-red-700">{stats?.outOfStockCount ?? 0}</p>
                            </div>
                        </div>

                        {flaggedItems.length > 0 ? (
                            <div>
                                <p className="text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">Needs Attention</p>
                                <div className="space-y-2">
                                    {paginatedFlaggedItems.map(item => {
                                        const s = getStockStyle(item)
                                        return (
                                            <div key={item.id} className={`flex items-center justify-between flex-wrap gap-y-1 py-2 px-3 ${s.bg} rounded-lg`}>
                                                <div className="flex-1 min-w-0 mr-3">
                                                    <p className="text-xs font-medium text-gray-700 truncate">
                                                        {item.ingredients?.name || 'Unknown'}
                                                    </p>
                                                    <div className="mt-1 w-full bg-white/60 rounded-full h-1.5">
                                                        <div
                                                            className={`h-1.5 rounded-full ${s.bar} transition-all`}
                                                            style={{ width: `${s.barPct}%` }}
                                                        />
                                                    </div>
                                                </div>
                                                <span className={`text-xs font-bold ${s.text} flex-shrink-0`}>
                                                    {item.available_stock} {item.ingredients?.unit || ''}
                                                </span>
                                            </div>
                                        )
                                    })}
                                </div>
                                {flaggedTotalPages > 1 && (
                                    <div className="flex items-center justify-between flex-wrap gap-2 mt-3">
                                        <span className="text-[11px] text-gray-400">
                                            Page {flaggedPage} of {flaggedTotalPages}
                                        </span>
                                        <div className="flex gap-1.5">
                                            <button
                                                disabled={flaggedPage === 1}
                                                onClick={() => setFlaggedPage(p => p - 1)}
                                                className="px-2.5 py-1 rounded-md text-[11px] border border-gray-200 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                                            >
                                                ← Prev
                                            </button>
                                            <button
                                                disabled={flaggedPage === flaggedTotalPages}
                                                onClick={() => setFlaggedPage(p => p + 1)}
                                                className="px-2.5 py-1 rounded-md text-[11px] border border-gray-200 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                                            >
                                                Next →
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 py-2 px-3 bg-green-50 rounded-lg">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-green-500 flex-shrink-0"><polyline points="20 6 9 17 4 12" /></svg>
                                <p className="text-xs text-green-600 font-medium">All stock levels are healthy</p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}